import {
  Anchor,
  Box,
  Button,
  Card,
  Collapse,
  Drawer,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconPlus,
  IconSearch,
} from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, type CategoryNode, type Product } from "../../lib/api";
import { categoryLeaves } from "../../lib/hierarchy";
import { formatINR } from "../../lib/money";
import {
  DataTable,
  EmptyState,
  ExportMenu,
  InlineError,
  LoadingSkeleton,
  NumField,
  RecordCard,
  StateBadge,
  useColumns,
  type Column,
} from "../../components/ui";
import { Page } from "../../shell/Page";

/**
 * The Store Manager's product master: where a product sits, the flat list of
 * them, and one drawer that edits the one you picked.
 *
 * WHAT CHANGED AND WHY (observation 4 of the second design handoff)
 *
 * This screen used to render the four-level hierarchy as sixteen nested
 * cards, each card holding the products at its level. The shape matched the
 * pickers - which was the argument for it - but it meant "find the SKU with
 * no price" was a scrolling exercise through cards that were mostly empty,
 * and comparing two products meant holding one in your head while you
 * scrolled to the other.
 *
 * Three panes instead, and each does one job:
 *
 *   WHERE IT SITS   the hierarchy, as a narrow count tree. Still the shape
 *                   the rest of the app selects through, but now it FILTERS
 *                   rather than contains, so the products stay in one list.
 *   THE LIST        one flat table, every product the filter admits, with the
 *                   columns you actually compare on side by side.
 *   THE DRAWER      the edit form, over the table rather than inside a row,
 *                   so the list does not reflow while you type.
 *
 * THE COUNTS IN THE TREE MATCH THE TABLE
 *
 * Every count is the number of rows clicking it would produce - the Show
 * filters are applied to the tree counts too. A count that ignored the active
 * filters would send somebody to a table that says something different, which
 * is the same defect as a dashboard number disagreeing with its list.
 *
 * TWO THINGS THIS SCREEN STILL CANNOT DO
 *
 * It cannot set a price. A price is a dated row in an append-only history
 * (Q7); the Price list screen owns that, and editing a price as a column here
 * would destroy the record of what was charged before.
 *
 * It cannot set stock. currentStockQty is a cache of the inventory ledger
 * (REQ-DATA-2), so a new product starts at zero and stock arrives through
 * Inventory as a receipt.
 *
 * Both are shown, read-only, under a heading that says they are owned
 * elsewhere, with a link to the screen that owns them - so the absence reads
 * as deliberate rather than missing.
 */

/** A product with its place in the tree flattened onto it. */
interface Row {
  product: Product;
  /** "AMARON › BATTERY › 4W › FLO", for the breadcrumb and the search. */
  path: string;
  /** Every ancestor id, so one click on a branch selects everything under it. */
  ancestors: string[];
}

/** Which products to show, independent of where they sit. */
interface Show {
  scope: "active" | "discontinued" | "all";
  /** Never priced. Ten of the 101 in the master list, on purpose. */
  noPrice: boolean;
  noHsn: boolean;
}

const ALL_PRODUCTS = "";

function flatten(
  nodes: CategoryNode[],
  trail: { ids: string[]; names: string[] } = { ids: [], names: [] },
): Row[] {
  return nodes.flatMap((node) => {
    const here = {
      ids: [...trail.ids, node.id],
      names: [...trail.names, node.name],
    };
    return [
      ...node.products.map((product) => ({
        product,
        path: here.names.join(" › "),
        ancestors: here.ids,
      })),
      ...flatten(node.children, here),
    ];
  });
}

function passesShow(product: Product, show: Show): boolean {
  if (show.scope === "active" && !product.isActive) return false;
  if (show.scope === "discontinued" && product.isActive) return false;
  if (show.noPrice && product.dealerPrice !== null) return false;
  if (show.noHsn && (product.hsnCode ?? "") !== "") return false;
  return true;
}

function passesQuery(row: Row, needle: string): boolean {
  if (!needle) return true;
  return (
    row.product.sku.toLowerCase().includes(needle) ||
    row.product.name.toLowerCase().includes(needle) ||
    row.path.toLowerCase().includes(needle)
  );
}

export function ProductAdmin() {
  const [tree, setTree] = useState<CategoryNode[]>();
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [query, setQuery] = useState("");
  const [node, setNode] = useState(ALL_PRODUCTS);
  const [show, setShow] = useState<Show>({
    scope: "active",
    noPrice: false,
    noHsn: false,
  });
  /** A product id, or "new" for the add form, or undefined for closed. */
  const [editing, setEditing] = useState<string | "new">();

  const refresh = useCallback(async () => {
    setError(undefined);
    try {
      // Inactive included: this is the screen where a discontinued product is
      // brought back, so hiding them would make that impossible.
      const { categories } = await api.catalogue(true);
      setTree(categories);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not load the catalogue.",
      );
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const rows = useMemo(() => (tree ? flatten(tree) : []), [tree]);
  const subCategories = useMemo(() => (tree ? categoryLeaves(tree) : []), [tree]);
  const needle = query.trim().toLowerCase();

  /** In the chosen branch and matching the search, before the Show filters. */
  const inScope = useMemo(
    () =>
      rows.filter(
        (row) =>
          (node === ALL_PRODUCTS || row.ancestors.includes(node)) &&
          passesQuery(row, needle),
      ),
    [rows, node, needle],
  );

  const visible = useMemo(
    () => inScope.filter((row) => passesShow(row.product, show)),
    [inScope, show],
  );

  /**
   * The tree's counts, keyed by node id.
   *
   * Counted over the SHOWN products and the current search, so a branch
   * reading "9" produces nine rows when clicked. Every ancestor of a product
   * gets it, which is what makes a parent's count the sum of its subtree.
   */
  const counts = useMemo(() => {
    const byNode = new Map<string, number>();
    for (const row of rows) {
      if (!passesShow(row.product, show)) continue;
      if (!passesQuery(row, needle)) continue;
      for (const id of row.ancestors) {
        byNode.set(id, (byNode.get(id) ?? 0) + 1);
      }
    }
    return byNode;
  }, [rows, show, needle]);

  const totalShown = useMemo(
    () =>
      rows.filter((row) => passesShow(row.product, show) && passesQuery(row, needle))
        .length,
    [rows, show, needle],
  );

  const selected =
    editing && editing !== "new"
      ? rows.find((row) => row.product.id === editing)
      : undefined;

  const breadcrumb = useMemo(() => {
    if (node === ALL_PRODUCTS) return "All products";
    const hit = rows.find((row) => row.ancestors.includes(node));
    if (!hit) return "All products";
    const depth = hit.ancestors.indexOf(node);
    return hit.path.split(" › ").slice(0, depth + 1).join(" › ");
  }, [node, rows]);

  async function update(
    product: Product,
    changes: Parameters<typeof api.updateProduct>[1],
  ) {
    setError(undefined);
    setNotice(undefined);
    try {
      await api.updateProduct(product.id, changes);
      await refresh();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not update that product.",
      );
      throw caught;
    }
  }

  const filters = (
    <WhereItSits
      tree={tree ?? []}
      counts={counts}
      total={totalShown}
      node={node}
      onNode={setNode}
      show={show}
      onShow={setShow}
      inScope={inScope}
    />
  );

  return (
    <Page
      title="Products"
      subtitle="The master list every module selects from"
      width="wide"
      actions={
        <Button
          size="sm"
          leftSection={<IconPlus size={16} stroke={2} />}
          onClick={() => setEditing("new")}
        >
          Add a product
        </Button>
      }
    >
      {notice && (
        <Text fz="md" c="teal.8">
          {notice}
        </Text>
      )}
      {error && <InlineError>{error}</InlineError>}

      {!tree && !error && <LoadingSkeleton variant="card" count={3} />}

      {tree && (
        <>
          {/* Phone: the filters collapse out of the way, because 260px of
              tree beside a table does not fit and a tree above the list
              pushes every product below the fold. */}
          <Box hiddenFrom="md">
            <MobileFilters
              breadcrumb={breadcrumb}
              count={visible.length}
              filters={filters}
            />
          </Box>

          <Group align="flex-start" gap={16} wrap="nowrap">
            <Box visibleFrom="md" w={260} style={{ flexShrink: 0 }}>
              {filters}
            </Box>

            <Stack gap={12} style={{ flex: 1, minWidth: 0 }}>
              <TextInput
                id="p-search"
                type="search"
                aria-label="Search products"
                value={query}
                onChange={(event) => setQuery(event.currentTarget.value)}
                placeholder="SKU, name or category"
                leftSection={<IconSearch size={16} stroke={1.8} />}
              />

              <Group gap={8} wrap="nowrap" align="center">
                <Text fz={13} c="gray.6" truncate>
                  {breadcrumb}
                </Text>
                <Text fz={13} c="gray.7" fw={600} className="num">
                  · {visible.length} {visible.length === 1 ? "product" : "products"}
                </Text>
                <Box style={{ flex: 1 }} />
                {/* Desktop only: the phone has no table to export and
                    nowhere sensible to put the file.

                    The count has come OUT of the label. It used to be there
                    because the file was what the category filter and the
                    search had left, and "Export" alone would have been read
                    as the whole master list. It now IS the whole master list,
                    including the discontinued products this screen hides, so
                    a count of what is visible would be the misleading half. */}
                <Box visibleFrom="md">
                  <ExportMenu dataset="products" />
                </Box>
              </Group>

              {visible.length === 0 ? (
                <Card withBorder padding={0}>
                  <EmptyState
                    title="Nothing matches"
                    description="The branch, the search and the Show filters are applied together."
                    // The way out, not just the news - a filter that empties
                    // the table is easy to reach by accident and the tree row
                    // that caused it may itself have been filtered away.
                    action={
                      <Button
                        variant="default"
                        onClick={() => {
                          setNode(ALL_PRODUCTS);
                          setQuery("");
                          setShow({ scope: "active", noPrice: false, noHsn: false });
                        }}
                      >
                        Show every active product
                      </Button>
                    }
                  />
                </Card>
              ) : (
                <>
                  <Box visibleFrom="md">
                    <ProductTable
                      rows={visible}
                      editing={editing}
                      onEdit={(id) => setEditing(id)}
                      onBringBack={(product) =>
                        void update(product, { isActive: true }).catch(() => {})
                      }
                    />
                  </Box>
                  <Box hiddenFrom="md">
                    <Stack gap={8}>
                      {visible.map((row) => (
                        <ProductCardRow
                          key={row.product.id}
                          row={row}
                          onEdit={() => setEditing(row.product.id)}
                        />
                      ))}
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          </Group>
        </>
      )}

      <Drawer
        opened={editing !== undefined}
        onClose={() => setEditing(undefined)}
        position="right"
        // The brief's 380px, and never wider than the screen - at 390px a
        // fixed 380 leaves a ten-pixel sliver of table nobody can use.
        size="min(380px, 100%)"
        // No dimming: on the desktop this opens OVER the table and you go on
        // reading the row you are editing. A border and the brief's one
        // shadow separate it instead.
        withOverlay={false}
        title={editing === "new" ? "Add a product" : "Edit product"}
        styles={{
          content: { boxShadow: "0 2px 8px rgba(33,37,41,.08)" },
          body: { paddingBottom: 24 },
        }}
      >
        {editing === "new" ? (
          <AddProductForm
            subCategories={subCategories}
            onAdded={(message) => {
              setNotice(message);
              setEditing(undefined);
              void refresh();
            }}
          />
        ) : selected ? (
          <EditProductForm
            // Keyed so switching rows resets the fields rather than carrying
            // the previous product's edits into the next one.
            key={selected.product.id}
            row={selected}
            subCategories={subCategories}
            onSave={async (changes) => {
              await update(selected.product, changes);
              setEditing(undefined);
            }}
            onDiscontinue={async () => {
              await update(selected.product, { isActive: false });
              setEditing(undefined);
            }}
            onBringBack={async () => {
              await update(selected.product, { isActive: true });
              setEditing(undefined);
            }}
          />
        ) : null}
      </Drawer>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Pane 1: where it sits
// ---------------------------------------------------------------------------

function WhereItSits({
  tree,
  counts,
  total,
  node,
  onNode,
  show,
  onShow,
  inScope,
}: {
  tree: CategoryNode[];
  counts: Map<string, number>;
  total: number;
  node: string;
  onNode: (id: string) => void;
  show: Show;
  onShow: (show: Show) => void;
  inScope: Row[];
}) {
  // What each Show option would give you, so a count is never a promise the
  // table breaks. Each is computed with the OTHER filters left in place and
  // its own removed - the question a filter chip answers is "how many if I
  // click this", not "how many are like this overall".
  const scopeCount = (scope: Show["scope"]) =>
    inScope.filter((row) => passesShow(row.product, { ...show, scope })).length;
  const flagCount = (flag: "noPrice" | "noHsn") =>
    inScope.filter((row) => passesShow(row.product, { ...show, [flag]: true })).length;

  return (
    <Stack gap={16}>
      <Card withBorder padding={0}>
        <Box px={12} py={10} style={{ borderBottom: "1px solid #f1f3f5" }}>
          <FrameLabel>Where it sits</FrameLabel>
        </Box>
        <Stack gap={0} p={6}>
          <TreeRow
            label="All products"
            count={total}
            depth={0}
            active={node === ALL_PRODUCTS}
            onClick={() => onNode(ALL_PRODUCTS)}
          />
          {tree.map((root) => (
            <TreeBranch
              key={root.id}
              node={root}
              depth={1}
              counts={counts}
              selected={node}
              onSelect={onNode}
            />
          ))}
        </Stack>
      </Card>

      <Card withBorder padding={0}>
        <Box px={12} py={10} style={{ borderBottom: "1px solid #f1f3f5" }}>
          <FrameLabel>Show</FrameLabel>
        </Box>
        <Stack gap={0} p={6}>
          <FilterRow
            label="Active"
            count={scopeCount("active")}
            active={show.scope === "active"}
            onClick={() => onShow({ ...show, scope: "active" })}
          />
          <FilterRow
            label="Discontinued"
            count={scopeCount("discontinued")}
            active={show.scope === "discontinued"}
            onClick={() => onShow({ ...show, scope: "discontinued" })}
          />
          <FilterRow
            label="Both"
            count={scopeCount("all")}
            active={show.scope === "all"}
            onClick={() => onShow({ ...show, scope: "all" })}
          />
          <Box my={6} style={{ borderTop: "1px solid #f1f3f5" }} />
          {/* These two are the reason this group exists. An unpriced product
              is invisible in a picker's price column and a missing HSN is a
              GST return that will not file, and neither shows up unless you
              can ask for it. */}
          <FilterRow
            label="No price set"
            count={flagCount("noPrice")}
            alarming
            active={show.noPrice}
            onClick={() => onShow({ ...show, noPrice: !show.noPrice })}
          />
          <FilterRow
            label="No HSN"
            count={flagCount("noHsn")}
            active={show.noHsn}
            onClick={() => onShow({ ...show, noHsn: !show.noHsn })}
          />
        </Stack>
      </Card>
    </Stack>
  );
}

function TreeBranch({
  node,
  depth,
  counts,
  selected,
  onSelect,
}: {
  node: CategoryNode;
  depth: number;
  counts: Map<string, number>;
  selected: string;
  onSelect: (id: string) => void;
}) {
  const count = counts.get(node.id) ?? 0;
  // A branch opens when it, or anything under it, is the selection - so
  // choosing a sub category from a search result does not leave it hidden.
  const containsSelection = useMemo(
    () => selected !== ALL_PRODUCTS && subtreeIds(node).includes(selected),
    [node, selected],
  );
  const [open, setOpen] = useState(false);
  const expanded = open || containsSelection;

  // A branch with nothing in it under the current filters is not drawn.
  // Sixteen empty levels is what this screen was replacing.
  //
  // Unless it holds the selection: a filter change that empties the chosen
  // branch would otherwise remove the only row that says where you are, and
  // the only one that can un-say it.
  if (count === 0 && !containsSelection) return null;

  return (
    <>
      <TreeRow
        label={node.name}
        count={count}
        depth={depth}
        active={selected === node.id}
        onClick={() => onSelect(node.id)}
        expandable={node.children.length > 0}
        expanded={expanded}
        onToggle={() => setOpen(!expanded)}
      />
      {node.children.length > 0 && (
        <Collapse in={expanded}>
          <Stack gap={0}>
            {node.children.map((child) => (
              <TreeBranch
                key={child.id}
                node={child}
                depth={depth + 1}
                counts={counts}
                selected={selected}
                onSelect={onSelect}
              />
            ))}
          </Stack>
        </Collapse>
      )}
    </>
  );
}

function subtreeIds(node: CategoryNode): string[] {
  return [node.id, ...node.children.flatMap(subtreeIds)];
}

function TreeRow({
  label,
  count,
  depth,
  active,
  onClick,
  expandable = false,
  expanded = false,
  onToggle,
}: {
  label: string;
  count: number;
  depth: number;
  active: boolean;
  onClick: () => void;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
}) {
  return (
    <Group gap={0} wrap="nowrap" style={{ paddingLeft: depth * 12 }}>
      {expandable ? (
        <UnstyledButton
          onClick={onToggle}
          aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
          aria-expanded={expanded}
          style={{
            width: 20,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--mantine-color-gray-6)",
            flexShrink: 0,
          }}
        >
          {expanded ? (
            <IconChevronDown size={14} stroke={2} />
          ) : (
            <IconChevronRight size={14} stroke={2} />
          )}
        </UnstyledButton>
      ) : (
        <Box w={20} style={{ flexShrink: 0 }} />
      )}
      <UnstyledButton
        onClick={onClick}
        aria-current={active ? "true" : undefined}
        px={8}
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 32,
          display: "flex",
          alignItems: "center",
          gap: 8,
          borderRadius: 6,
          background: active ? "#edf2ff" : "transparent",
          color: active ? "#3b5bdb" : "var(--mantine-color-gray-8)",
        }}
      >
        <Text fz={13} fw={active ? 600 : 400} style={{ color: "inherit", flex: 1 }} truncate>
          {label}
        </Text>
        <Text fz={12} c={active ? "indigo.7" : "gray.6"} className="num" fw={600}>
          {count}
        </Text>
      </UnstyledButton>
    </Group>
  );
}

function FilterRow({
  label,
  count,
  active,
  alarming = false,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  /** Something that should not be true of a product, so its count is red. */
  alarming?: boolean;
  onClick: () => void;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      aria-pressed={active}
      px={8}
      style={{
        minHeight: 32,
        display: "flex",
        alignItems: "center",
        gap: 8,
        borderRadius: 6,
        background: active ? "#edf2ff" : "transparent",
        color: active ? "#3b5bdb" : "var(--mantine-color-gray-8)",
      }}
    >
      <Text fz={13} fw={active ? 600 : 400} style={{ color: "inherit", flex: 1 }}>
        {label}
      </Text>
      <Text
        fz={12}
        fw={600}
        className="num"
        c={alarming && count > 0 ? "red.8" : active ? "indigo.7" : "gray.6"}
      >
        {count}
      </Text>
    </UnstyledButton>
  );
}

function FrameLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text fz={12} fw={700} c="gray.6" tt="uppercase" style={{ letterSpacing: ".06em" }}>
      {children}
    </Text>
  );
}

function MobileFilters({
  breadcrumb,
  count,
  filters,
}: {
  breadcrumb: string;
  count: number;
  filters: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Card withBorder padding={0}>
      <UnstyledButton
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        px={14}
        py={12}
        w="100%"
        style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 44 }}
      >
        <Box style={{ flex: 1, minWidth: 0 }}>
          <FrameLabel>Showing</FrameLabel>
          <Text fz={14} fw={600} truncate>
            {breadcrumb}
          </Text>
        </Box>
        <Text fz={13} c="gray.6" className="num">
          {count}
        </Text>
        {open ? (
          <IconChevronDown size={16} stroke={2} color="#868e96" />
        ) : (
          <IconChevronRight size={16} stroke={2} color="#868e96" />
        )}
      </UnstyledButton>
      <Collapse in={open}>
        <Box p={10} style={{ borderTop: "1px solid #f1f3f5" }}>
          {filters}
        </Box>
      </Collapse>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Pane 2: the list
// ---------------------------------------------------------------------------

/**
 * The six DATA columns, at module scope so the table and the export read the
 * same definitions.
 *
 * The action column is deliberately NOT here: ProductTable appends it, and
 * the export never sees it. That makes "an action is not data" structural
 * rather than a habit somebody has to remember - the alternative was one
 * array with a column the exporter had to know to skip.
 */
const PRODUCT_COLUMNS: Column<Row>[] = [
    {
      key: "SKU",
      header: "SKU",
      width: "230px",
      render: ({ product }) => (
        <Text
          fz={13}
          fw={600}
          className="num"
          truncate
          style={{ color: "inherit" }}
        >
          {product.sku}
        </Text>
      ),
    },
    {
      key: "Name",
      header: "Name",
      width: "auto",
      render: ({ product }) => (
        <Group gap={6} wrap="nowrap">
          <Text fz={14} truncate style={{ color: "inherit" }}>
            {product.name}
          </Text>
          {/* A labelled chip, not merely a fainter row: a discontinued
              product that only looks quieter is one people read as normal. */}
          {!product.isActive && (
            <StateBadge state="svcCancelled" label="Discontinued" />
          )}
        </Group>
      ),
    },
    {
      key: "Dealer price",
      header: "Dealer price",
      width: "120px",
      align: "right",
      render: ({ product }) =>
        product.dealerPrice ? (
          <Text fz={14} className="num" style={{ color: "inherit" }}>
            {formatINR(product.dealerPrice)}
          </Text>
        ) : (
          <Text fz={13} fw={600} c="red.8">
            No price set
          </Text>
        ),
      // The number alone, not the formatted rupee string: a spreadsheet
      // cannot add up "₹4,180.00", and an unpriced product exports as
      // empty rather than as zero, which would read as free.
    },
    {
      key: "In stock",
      header: "In stock",
      width: "110px",
      align: "right",
      render: ({ product }) => {
        const stock = product.currentStockQty ?? 0;
        return (
          <Text
            fz={14}
            className="num"
            fw={stock === 0 ? 700 : 400}
            c={stock === 0 ? "red.8" : undefined}
              style={stock === 0 ? undefined : { color: "inherit" }}
          >
            {stock}
          </Text>
        );
      },
    },
    {
      key: "GST",
      header: "GST",
      width: "100px",
      align: "right",
      render: ({ product }) => (
        <Text fz={14} className="num" style={{ color: "inherit" }}>
          {product.gstRate}%
        </Text>
      ),
    },
    {
      key: "HSN",
      header: "HSN",
      width: "130px",
      render: ({ product }) =>
        product.hsnCode ? (
          <Text fz={14} className="num" style={{ color: "inherit" }}>
            {product.hsnCode}
          </Text>
        ) : (
          <Text fz={13} c="gray.6">
            —
          </Text>
        ),
    },
];

/**
 * The centre pane, on the shared DataTable.
 *
 * It had its own <table> first, written before the other three screens
 * needed one. That table is now components/ui/DataTable, and this renders
 * through it - so the keyboard behaviour, the cursor and the CSV escaping are
 * the same here as in the service queue rather than being a second opinion.
 */
function ProductTable({
  rows,
  editing,
  onEdit,
  onBringBack,
}: {
  rows: Row[];
  editing: string | "new" | undefined;
  onEdit: (id: string) => void;
  onBringBack: (product: Product) => void;
}) {
  const columns = useColumns<Row>(
    () => [
      ...PRODUCT_COLUMNS,
      {
        key: "action",
        header: "",
        width: "130px",
        render: ({ product }) =>
          product.isActive ? (
            <Button
              variant={editing === product.id ? "light" : "default"}
              size="compact-sm"
              onClick={() => onEdit(product.id)}
            >
              {editing === product.id ? "Editing" : "Edit"}
            </Button>
          ) : (
            <Button
              variant="default"
              size="compact-sm"
              onClick={() => onBringBack(product)}
            >
              Bring back
            </Button>
          ),
        // No `value`: an action is not data, and "Bring back" in a
        // spreadsheet column would be nonsense that changed with the label.
      },
    ],
    [editing, onEdit, onBringBack],
  );

  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(row) => row.product.id}
      rowMuted={(row) => !row.product.isActive}
      // Enter opens the drawer for the row under the cursor, which is the
      // same thing the Edit button does - so the keyboard is not a second,
      // lesser way through the screen.
      onOpen={(row) => onEdit(row.product.id)}
      activeKey={editing && editing !== "new" ? editing : undefined}
      caption={`Products: ${rows.length}`}
      emptyLabel="Nothing matches."
    />
  );
}

/** The phone's row. A table at 390px is a table nobody can read. */
function ProductCardRow({ row, onEdit }: { row: Row; onEdit: () => void }) {
  const { product } = row;
  const stock = product.currentStockQty ?? 0;
  return (
    <RecordCard
      primary={product.sku}
      secondary={product.name}
      onClick={onEdit}
      ariaLabel={`Edit ${product.sku}`}
      meta={
        <Group gap={8} wrap="wrap">
          {product.dealerPrice ? (
            <Text fz={13} className="num">
              {formatINR(product.dealerPrice)}
            </Text>
          ) : (
            <Text fz={13} fw={600} c="red.8">
              No price set
            </Text>
          )}
          <Text fz={13} className="num" fw={stock === 0 ? 700 : 400} c={stock === 0 ? "red.8" : "gray.6"}>
            {stock} in stock
          </Text>
          <Text fz={13} c="gray.6" className="num">
            {product.gstRate}% GST
            {product.hsnCode ? ` · HSN ${product.hsnCode}` : " · no HSN"}
          </Text>
        </Group>
      }
      badges={
        product.isActive ? undefined : (
          <StateBadge state="svcCancelled" label="Discontinued" />
        )
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Pane 3: the drawer
// ---------------------------------------------------------------------------

/**
 * The notice goes FIRST, above the fields.
 *
 * It explains the one thing the form refuses to do, and a rule stated after
 * the fields is a rule read after somebody has already looked for the field
 * and concluded it is missing.
 */
function SkuNotice() {
  return (
    <Card
      withBorder
      padding={12}
      mb={16}
      style={{ background: "#fff9db", borderColor: "#ffe066" }}
    >
      <Text fz={13} c="gray.8">
        The SKU is not editable — invoices already raised refer to it. A product
        created wrongly is discontinued and replaced rather than renamed under
        the same code.
      </Text>
    </Card>
  );
}

function EditProductForm({
  row,
  subCategories,
  onSave,
  onDiscontinue,
  onBringBack,
}: {
  row: Row;
  subCategories: { id: string; path: string }[];
  onSave: (changes: Parameters<typeof api.updateProduct>[1]) => Promise<void>;
  onDiscontinue: () => Promise<void>;
  onBringBack: () => Promise<void>;
}) {
  const { product } = row;
  const [name, setName] = useState(product.name);
  const [hsnCode, setHsnCode] = useState(product.hsnCode ?? "");
  const [gstRate, setGstRate] = useState(String(product.gstRate));
  const [reorderLevel, setReorderLevel] = useState(
    product.reorderLevel === null || product.reorderLevel === undefined
      ? ""
      : String(product.reorderLevel),
  );
  const [categoryId, setCategoryId] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const gstValid = Number.isFinite(Number(gstRate)) && Number(gstRate) >= 0;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        setSaving(true);
        void onSave({
          name: name.trim(),
          hsnCode: hsnCode.trim() === "" ? null : hsnCode.trim(),
          gstRate: Number(gstRate),
          reorderLevel: reorderLevel.trim() === "" ? null : Number(reorderLevel),
          ...(categoryId ? { categoryId } : {}),
        })
          .catch(() => {})
          .finally(() => setSaving(false));
      }}
    >
      <SkuNotice />

      <Stack gap="sm">
        <TextInput
          label="SKU"
          value={product.sku}
          readOnly
          disabled
          className="num"
        />
        <TextInput
          id={`n-${product.id}`}
          label="Name"
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
          maxLength={200}
        />
        <TextInput
          id={`h-${product.id}`}
          label="HSN code"
          value={hsnCode}
          onChange={(event) => setHsnCode(event.currentTarget.value)}
          maxLength={10}
          className="num"
          placeholder="8507"
        />
        <NumField
          id={`g-${product.id}`}
          label="GST %"
          min={0}
          max={100}
          value={gstRate}
          onChange={(value) => setGstRate(String(value))}
          error={!gstValid ? "Enter a percentage, e.g. 18." : undefined}
        />
        <NumField
          id={`r-${product.id}`}
          label="Reorder level"
          description="Blank for none — it can then only show as out of stock, never as low."
          min={0}
          step={1}
          allowDecimal={false}
          value={reorderLevel}
          onChange={(value) => setReorderLevel(String(value))}
        />
        <Select
          id={`c-${product.id}`}
          label="Sub category"
          description={`Currently ${row.path}.`}
          placeholder="Leave where it is"
          value={categoryId || null}
          onChange={(value) => setCategoryId(value ?? "")}
          searchable
          clearable
          data={subCategories.map((line) => ({ value: line.id, label: line.path }))}
        />

        <Box mt={8}>
          <FrameLabel>Owned elsewhere</FrameLabel>
        </Box>
        <Card withBorder padding={12}>
          <Stack gap={10}>
            <OwnedElsewhere
              label="Dealer price"
              value={
                product.dealerPrice ? (
                  <Text fz={15} fw={600} className="num">
                    {formatINR(product.dealerPrice)}
                  </Text>
                ) : (
                  <Text fz={14} fw={600} c="red.8">
                    No price set
                  </Text>
                )
              }
              note={
                product.priceEffectiveFrom
                  ? `In force from ${new Date(product.priceEffectiveFrom).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`
                  : "A price is a dated entry, so there is a record of what was charged before."
              }
              to="/prices"
              linkLabel="Price list"
            />
            <Box style={{ borderTop: "1px solid #f1f3f5" }} />
            <OwnedElsewhere
              label="In stock"
              value={
                <Text fz={15} fw={600} className="num">
                  {product.currentStockQty ?? 0}
                </Text>
              }
              note="A cache of the ledger — stock only moves by a receipt or an issue."
              to="/inventory/movements"
              linkLabel="Movements"
            />
          </Stack>
        </Card>

        <Group gap="sm" mt={8} grow>
          {product.isActive ? (
            <Button
              variant="default"
              loading={busy}
              onClick={() => {
                setBusy(true);
                void onDiscontinue().catch(() => {}).finally(() => setBusy(false));
              }}
            >
              Discontinue
            </Button>
          ) : (
            <Button
              variant="default"
              loading={busy}
              onClick={() => {
                setBusy(true);
                void onBringBack().catch(() => {}).finally(() => setBusy(false));
              }}
            >
              Bring back
            </Button>
          )}
          <Button type="submit" loading={saving} disabled={!gstValid}>
            Save changes
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

function OwnedElsewhere({
  label,
  value,
  note,
  to,
  linkLabel,
}: {
  label: string;
  value: React.ReactNode;
  note: string;
  to: string;
  linkLabel: string;
}) {
  return (
    <Stack gap={2}>
      <Group justify="space-between" wrap="nowrap">
        <Text fz={13} c="gray.7">
          {label}
        </Text>
        {value}
      </Group>
      <Text fz={12} c="gray.6">
        {note}{" "}
        <Anchor component={Link} to={to} fz={12} fw={600}>
          {linkLabel}
        </Anchor>
      </Text>
    </Stack>
  );
}

function AddProductForm({
  subCategories,
  onAdded,
}: {
  subCategories: { id: string; path: string }[];
  onAdded: (message: string) => void;
}) {
  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [hsnCode, setHsnCode] = useState("");
  const [gstRate, setGstRate] = useState("18");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const gstValid = Number.isFinite(Number(gstRate)) && Number(gstRate) >= 0;
  const canAdd =
    sku.trim().length >= 2 && name.trim().length >= 2 && categoryId !== "" && gstValid;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        setError(undefined);
        setBusy(true);
        api
          .createProduct({
            sku: sku.trim(),
            name: name.trim(),
            categoryId,
            ...(hsnCode.trim() ? { hsnCode: hsnCode.trim() } : {}),
            gstRate: Number(gstRate),
          })
          .then(({ product }) =>
            onAdded(
              `Added ${product.sku}. Set its price on the price list, and ` +
                `receive stock through Inventory — it starts with neither.`,
            ),
          )
          .catch((caught: unknown) =>
            setError(
              caught instanceof Error ? caught.message : "Could not add that product.",
            ),
          )
          .finally(() => setBusy(false));
      }}
    >
      <SkuNotice />
      {error && <InlineError>{error}</InlineError>}

      <Stack gap="sm">
        <TextInput
          id="p-sku"
          label="SKU"
          withAsterisk
          value={sku}
          onChange={(event) => setSku(event.currentTarget.value)}
          placeholder="AAM-FL-00040B20L"
          maxLength={60}
          className="num"
          description="The code every invoice line will refer to."
        />
        <TextInput
          id="p-name"
          label="Name"
          withAsterisk
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
          maxLength={200}
        />
        <Select
          id="p-category"
          label="Sub category"
          withAsterisk
          placeholder="Choose where it sits"
          value={categoryId || null}
          onChange={(value) => setCategoryId(value ?? "")}
          searchable
          data={subCategories.map((line) => ({ value: line.id, label: line.path }))}
        />
        <TextInput
          id="p-hsn"
          label="HSN code"
          description="Optional."
          value={hsnCode}
          onChange={(event) => setHsnCode(event.currentTarget.value)}
          placeholder="8507"
          maxLength={10}
          className="num"
        />
        <NumField
          id="p-gst"
          label="GST %"
          withAsterisk
          min={0}
          max={100}
          value={gstRate}
          onChange={(value) => setGstRate(String(value))}
          error={!gstValid ? "Enter a percentage, e.g. 18." : undefined}
        />
        <Button type="submit" size="md" fullWidth loading={busy} disabled={!canAdd}>
          Add product
        </Button>
        <Text fz="sm" c="gray.6">
          No price and no opening stock here on purpose. A price is a dated
          entry on the{" "}
          <Anchor component={Link} to="/prices">
            price list
          </Anchor>
          , and stock only moves by a receipt in{" "}
          <Anchor component={Link} to="/inventory">
            Inventory
          </Anchor>
          .
        </Text>
      </Stack>
    </form>
  );
}
